import type { FinishLevel, Money, PricingLineItem } from "../types/index.js";

export interface LineItemCalculationParams {
  /**
   * Quantity in the item's own unit: square feet for "sqft", linear feet
   * for "linear_ft", a count for "item"/"fixture"/"room". Ignored for
   * "project"/"allowance" (always treated as 1) and for "percentage"
   * (driven by baseAmountForPercentage instead). Defaults to 1 if omitted
   * for a unit that needs it - a warning is attached when that happens,
   * since silently defaulting a square-footage quantity to 1 is usually
   * not what's intended.
   */
  quantity?: number;
  finishLevel?: FinishLevel;
  /** Regional multiplier already resolved (e.g. via resolveRegionalMultiplier), unless the item overrides it. */
  regionalMultiplier?: number;
  /** Required when the line item's unit is "percentage": the dollar amount to take the percentage of. */
  baseAmountForPercentage?: number;
}

export interface LineItemCalculationResult {
  lineItemId: string;
  low: Money | null;
  typical: Money | null;
  high: Money | null;
  minimum: Money | null;
  maximum: Money | null;
  appliedMultiplier: {
    complexity: number;
    finishLevel: number;
    regional: number;
    combined: number;
  };
  quantityApplied: number;
  warnings: string[];
}

function money(amount: number): Money {
  return { amount: Math.round(amount), currency: "CAD" };
}

function applyMultiplier(base: number | null, quantity: number, multiplier: number): number | null {
  if (base === null) return null;
  return base * quantity * multiplier;
}

/**
 * Computes an actual dollar figure for one pricing line item, given
 * quantity/finish-level/regional context. This is the "pricing
 * methodology" logic referenced in docs/pricing-methodology.md: the
 * calculation performed differs by `unit`, since not every renovation
 * category is naturally a square-footage calculation.
 *
 *   - sqft / linear_ft: base cost range x quantity x multipliers
 *   - item / fixture / room: base cost range x count x multipliers
 *     (count defaults to 1 - e.g. "1 vanity", "1 toilet")
 *   - project / allowance: base cost range x multipliers, quantity ignored
 *     (a flat planning allowance regardless of size)
 *   - percentage: `baseAmountForPercentage` x (cost range / 100) x
 *     multipliers - no quantity involved at all
 *
 * This function does not look anything up (no catalog access, no
 * regional table access) - it's a pure function of one line item plus
 * the context passed in. It lives in `core` (moved here in Phase 2, was
 * previously in `@reno-estimator/pricing-data`) because the calculation
 * engine (`packages/core/src/calculation/engine.ts`) needs to call it
 * directly, and `core` cannot depend on `pricing-data` (see
 * docs/interfaces.md). `@reno-estimator/pricing-data` re-exports this
 * function unchanged so existing callers of that package are unaffected.
 */
export function calculateLineItemCost(
  item: PricingLineItem,
  params: LineItemCalculationParams = {}
): LineItemCalculationResult {
  const warnings: string[] = [];

  const complexityMultiplier = item.multipliers.complexity;
  const finishLevelMultiplier = params.finishLevel
    ? item.multipliers.finishLevel[params.finishLevel] ?? 1.0
    : 1.0;
  const regionalMultiplier = item.multipliers.regionalOverride ?? params.regionalMultiplier ?? 1.0;
  const combinedMultiplier = complexityMultiplier * finishLevelMultiplier * regionalMultiplier;

  let quantity = 1;
  let low = item.costs.low;
  let typical = item.costs.typical;
  let high = item.costs.high;
  let minimum = item.costs.minimum;
  let maximum = item.costs.maximum;
  let isPercentageOfBase = false;

  if (item.unit === "sqft" || item.unit === "linear_ft") {
    if (params.quantity === undefined) {
      warnings.push(`No quantity provided for a "${item.unit}" line item; defaulted to 1 ${item.unit}.`);
    }
    quantity = params.quantity ?? 1;
  } else if (item.unit === "item" || item.unit === "fixture" || item.unit === "room") {
    quantity = params.quantity ?? 1;
  } else if (item.unit === "project" || item.unit === "allowance") {
    if (params.quantity !== undefined && params.quantity !== 1) {
      warnings.push(`Quantity was provided for a "${item.unit}" line item but is ignored - it is priced as a flat amount.`);
    }
    quantity = 1;
  } else if (item.unit === "percentage") {
    isPercentageOfBase = true;
    if (params.baseAmountForPercentage === undefined) {
      warnings.push('unit is "percentage" but no baseAmountForPercentage was provided; cost cannot be computed.');
      low = typical = high = minimum = maximum = null;
    } else {
      const base = params.baseAmountForPercentage;
      low = low !== null ? (base * low) / 100 : null;
      typical = typical !== null ? (base * typical) / 100 : null;
      high = high !== null ? (base * high) / 100 : null;
      minimum = minimum !== null ? (base * minimum) / 100 : null;
      maximum = maximum !== null ? (base * maximum) / 100 : null;
    }
    quantity = 1;
  }

  // A "percentage" line item's baseAmountForPercentage is always another
  // already-fully-computed line item's dollar result (e.g. demolition
  // priced as a percentage of the base renovation's typical cost) - which
  // already has the regional multiplier baked in. Re-applying
  // regionalMultiplier here on top of that would double-count it (a
  // Toronto 1.18x, say, would compound to ~1.39x). Complexity and finish
  // level are still applied once here, since those describe this specific
  // percentage-priced scope of work (e.g. demolition's own complexity),
  // not the base item's.
  const effectiveMultiplier = isPercentageOfBase ? complexityMultiplier * finishLevelMultiplier : combinedMultiplier;

  const computedLow = applyMultiplier(low, quantity, effectiveMultiplier);
  const computedTypical = applyMultiplier(typical, quantity, effectiveMultiplier);
  const computedHigh = applyMultiplier(high, quantity, effectiveMultiplier);
  const computedMin = applyMultiplier(minimum, quantity, effectiveMultiplier);
  const computedMax = applyMultiplier(maximum, quantity, effectiveMultiplier);

  if (item.confidenceLevel === "administrator_sample_value") {
    warnings.push("This line item is an administrator supplied sample value, not verified Canadian pricing data.");
  }

  return {
    lineItemId: item.id,
    low: computedLow !== null ? money(computedLow) : null,
    typical: computedTypical !== null ? money(computedTypical) : null,
    high: computedHigh !== null ? money(computedHigh) : null,
    minimum: computedMin !== null ? money(computedMin) : null,
    maximum: computedMax !== null ? money(computedMax) : null,
    appliedMultiplier: {
      complexity: complexityMultiplier,
      finishLevel: finishLevelMultiplier,
      // Reported as 1.0 (not applied) for a percentage-of-base item, since
      // the base amount it was computed from already carries the regional
      // adjustment - see the isPercentageOfBase note above.
      regional: isPercentageOfBase ? 1.0 : regionalMultiplier,
      combined: effectiveMultiplier
    },
    quantityApplied: quantity,
    warnings
  };
}

/** Sums the typical (and low/high) values across several computed line items - e.g. to build a project subtotal. */
export function sumLineItemCosts(results: LineItemCalculationResult[]): { low: Money; typical: Money; high: Money } {
  const sum = (pick: (r: LineItemCalculationResult) => Money | null) =>
    results.reduce((total, r) => total + (pick(r)?.amount ?? 0), 0);

  return {
    low: money(sum((r) => r.low)),
    typical: money(sum((r) => r.typical)),
    high: money(sum((r) => r.high))
  };
}
